
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import InfoBoxWarning from '@components/shared/ui/InfoBoxWarning.vue';
import SettingsTitleBar from '@components/settings/SettingsTitleBar.vue';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppHeader from "@components/new-styleguide/AppHeader.vue";
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@/utils/types';
import { Pilot, PilotMutation } from '@/generated-clients/myTrips';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import IconColors from '@/config/IconColors';

const SettingsModule = namespace('Settings');

@Component({
  name: 'PilotsOverview',
  components: {
    SettingsPanel,
    AppListItem,
    InfoBoxWarning,
    AppContent,
    SettingsTitleBar,
    AppPage,
    AppHeader
  }
})
export default class PilotsOverview extends Vue {
  @SettingsModule.Action('createPilot')
  private createPilot;

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchPilotSuggestions')
  private fetchPilotSuggestions;

  @SettingsModule.Getter('getPilots')
  private pilots: Pilot[];

  @SettingsModule.Getter('getPilotSuggestions')
  private pilotSuggestions: PilotMutation[];

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getPilotSuggestionsStatus')
  private pilotSuggestionsStatus: LoadState;

  private disabled = false;

  private created() {
    this.fetchPilots();
    this.fetchPilotSuggestions();
  }

  get backButton() {
    return { text: 'Zurück zu Anpassen', location: { name: 'mySettings' } };
  }

  get addButton() {
    return { text: 'Neue Person anlegen', location: { name: 'pilotsAdd' } };
  }

  get pilotsHeaderText() {
    return 'Personen können Fahrten manuell oder durch Regeln zugeordnet werden. Dadurch kann das Fahrverhalten der Personen in der Statistik verglichen werden.';
  }

  get createItem(): ListItem {
    return { title: 'Neue Person anlegen', tagIcon: { value: 'tag_plus', color: '#FFDC00', fill: 'black' }, location: { name: 'pilotsAdd' } };
  }

  get pilotItems(): ListItem[] {
    if (this.isLoading) {
      return []
    }

    return this.pilots.map(
      p => ({
        id: p.id,
        title: p.nickname || `${p.firstname} ${p.lastname}`,
        letterIcon: { value: p.initial, color: p.color, fill: 'white' },
        location: { name: 'pilotEdit', params: { id: p.id.toString() } },
        hoverText: 'Person bearbeiten'
      })
    );
  }

  get suggestionsHeaderText() {
    return 'Die hier gelisteten vorgeschlagenen Personen werden aus den assoziierten Bezugspersonen ihres Fahrzeuges und den mitgeschützten Personen ihrer Mitgliedschaft bezogen.';
  }

  get pilotSuggestionsItems(): ListItem[] {
    if (this.pilotSuggestionsStatus != LoadState.Success) {
      return [];
    }

    return this.pilotSuggestions.map(ps => ({
      title: `${ps.firstname} ${ps.lastname}`, letterIcon: { value: ps.initial, color: ps.color, fill: 'white' }, id: ps.cknr
    }));
  }

  private async addSuggestion(index: number) {
    const randomIndex = Math.floor(Math.random() * IconColors.codes.length);
    const randomColor = IconColors.codes[randomIndex];
    this.disabled = true;

    try {
      await this.createPilot({ pilot: { ...this.pilotSuggestions[index], color: randomColor } });
    } finally {
      this.disabled = false;
    }
  }

  get errorLoading() {
    return this.pilotsStatus === LoadState.Error;
  }

  get isLoading() {
    return this.pilotsStatus === LoadState.Loading || this.pilotSuggestionsStatus === LoadState.Loading;
  }
}
